import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { API_BASE_URL, apiGet, apiUpload, fetchAllPaginated } from '../services/api';
import { useEffectiveCity } from '../hooks/useEffectiveCity';
import SearchableSelect from '../components/SearchableSelect';

function Inputs() {
  const { user } = useAuth();
  // Referência Técnica só tem acesso ao upload de Alunos (não ao de Gabaritos).
  const isReferenciaTecnica = user?.role === ROLES.REFERENCIA_TECNICA;
  const { darkMode } = useTheme();
  // Cidade em foco (para o admin, a escolhida no menu lateral).
  const effectiveCity = useEffectiveCity();
  const cityId = effectiveCity?.id || null;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('alunos');
  const [stats, setStats] = useState({
    schools: 0,
    classes: 0,
    students: 0,
    exams: 0,
  });

  // Estado do upload de alunos
  const [uploadStatus, setUploadStatus] = useState({
    alunos: { file: null, progress: 0, status: null, message: '', result: null },
    gabaritos: { file: null, progress: 0, status: null, message: '', result: null },
  });

  // Estado para turmas e provas
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableExams, setAvailableExams] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedExamId, setSelectedExamId] = useState('');

  // Ref para input de arquivo
  const fileInputRefs = {
    alunos: useRef(null),
    gabaritos: useRef(null),
  };

  // Fetch stats, classes and exams
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Turmas e provas alimentam os seletores, então vêm TODAS as páginas:
        // a API devolve 100 por vez, e a turma que ficava depois da 100ª em
        // ordem alfabética (as "Turma A Noite EJA", por exemplo) simplesmente
        // não aparecia para escolher. Escolas e alunos só entram na contagem.
        const cityParams = cityId ? { city: cityId } : {};
        const [schoolsRes, classes, studentsRes, exams] = await Promise.all([
          apiGet('/schools/', { ...cityParams, page_size: 1 }).catch(() => ({ count: 0, results: [] })),
          fetchAllPaginated('/classes/', cityParams).catch(() => []),
          apiGet('/students/', { ...cityParams, page_size: 1 }).catch(() => ({ count: 0, results: [] })),
          fetchAllPaginated('/exams/').catch(() => []),
        ]);

        setStats({
          schools: schoolsRes.count || schoolsRes.results?.length || 0,
          classes: classes.length,
          students: studentsRes.count || studentsRes.results?.length || 0,
          exams: exams.length,
        });

        setAvailableClasses(classes);
        setAvailableExams(exams);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };

    fetchData();
  }, [cityId]);

  // Upload configurations
  const uploadConfigs = {
    alunos: {
      title: 'Upload de Alunos',
      endpoint: '/students/bulk_import/',
      acceptedFormats: ['.csv', '.xlsx', '.xls'],
      columns: [
        { name: 'ESTUDANTE_NOME', desc: 'Nome do aluno (obrigatório)', required: true },
        { name: 'ESTUDANTE_CPF', desc: 'CPF ou número de matrícula (obrigatório, único)', required: true },
        { name: 'DATA_INICIO_MATRICULA', desc: 'Data de matrícula (DD/MM/YYYY)' },
        { name: 'ESTUDANTE_DT_NASCIMENTO', desc: 'Data de nascimento (DD/MM/YYYY)' },
        { name: 'ESTUDANTE_MAE_NOME', desc: 'Nome da mãe' },
        { name: 'ESTUDANTE_E_MAIL', desc: 'E-mail do estudante' },
        { name: 'ESTUDANTE_GENERO', desc: 'Gênero (Masculino/Feminino)' },
        { name: 'ESTUDANTE_RACA_COR', desc: 'Raca/cor' },
        { name: 'ESTUDANTE_TRANSTORNO_ESPECTRO_AUTISTA', desc: 'TEA (1=Sim, 0=Não)' },
        { name: 'ESTUDANTE_DEFICIENCIA_*', desc: 'Deficiências (1=Sim, 0=Não)' },
      ],
      requiresClassSelection: true,
    },
    gabaritos: {
      title: 'Upload de Gabaritos dos ALunos',
      endpoint: '/exam-applications/process-omr-cards/',
      acceptedFormats: ['.pdf'],
      requiresExamSelection: true,
      requiresClassSelection: true,
    },
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-[#13983D]', 'bg-[#13983D]/10');
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-[#13983D]', 'bg-[#13983D]/10');
  }, []);

  const handleDrop = useCallback((e, tabType) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-[#13983D]', 'bg-[#13983D]/10');
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0], tabType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = (file, tabType) => {
    const config = uploadConfigs[tabType];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!config.acceptedFormats.includes(fileExtension)) {
      setUploadStatus(prev => ({
        ...prev,
        [tabType]: {
          ...prev[tabType],
          file: null,
          status: 'error',
          message: `Formato inválido. Use: ${config.acceptedFormats.join(', ')}`,
        }
      }));
      return;
    }

    setUploadStatus(prev => ({
      ...prev,
      [tabType]: {
        ...prev[tabType],
        file,
        status: 'ready',
        message: `Arquivo selecionado: ${file.name}`,
        result: null,
      }
    }));
  };

  const handleUpload = async (tabType) => {
    const status = uploadStatus[tabType];
    const config = uploadConfigs[tabType];

    if (!status.file) {
      setUploadStatus(prev => ({
        ...prev,
        [tabType]: {
          ...prev[tabType],
          status: 'error',
          message: 'Selecione um arquivo primeiro',
        }
      }));
      return;
    }

    // Validação de turma
    if (config.requiresClassSelection && !selectedClassId) {
      setUploadStatus(prev => ({
        ...prev,
        [tabType]: {
          ...prev[tabType],
          status: 'error',
          message: 'Selecione uma turma antes de fazer o upload',
        }
      }));
      return;
    }

    // Validação de prova (gabaritos)
    if (config.requiresExamSelection && !selectedExamId) {
      setUploadStatus(prev => ({
        ...prev,
        [tabType]: {
          ...prev[tabType],
          status: 'error',
          message: 'Selecione uma prova antes de fazer o upload',
        }
      }));
      return;
    }

    setLoading(true);
    setUploadStatus(prev => ({
      ...prev,
      [tabType]: {
        ...prev[tabType],
        status: 'uploading',
        progress: 0,
        message: tabType === 'gabaritos' ? 'Processando cartões de Gabaritos...' : 'Enviando arquivo...',
      }
    }));

    try {
      const formData = new FormData();
      formData.append('file', status.file);

      if (tabType === 'alunos' && selectedClassId) {
        formData.append('id_class', selectedClassId);
      }

      if (tabType === 'gabaritos') {
        formData.append('exam_id', selectedExamId);
        formData.append('class_id', selectedClassId);
      }

      const result = await apiUpload(config.endpoint, formData, (progress) => {
        setUploadStatus(prev => ({
          ...prev,
          [tabType]: {
            ...prev[tabType],
            progress,
          }
        }));
      });

      setUploadStatus(prev => ({
        ...prev,
        [tabType]: {
          ...prev[tabType],
          status: 'success',
          progress: 100,
          message: tabType === 'gabaritos'
            ? `Processamento concluído! ${result.saved || 0} alunos processados com sucesso.`
            : (result.message || 'Upload realizado com sucesso!'),
          result,
        }
      }));

      // Refresh stats
      if (tabType === 'alunos') {
        const res = await apiGet('/students/', { ...(cityId ? { city: cityId } : {}), page_size: 1 })
          .catch(() => ({ count: 0, results: [] }));
        setStats(prev => ({ ...prev, students: res.count || res.results?.length || 0 }));
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(prev => ({
        ...prev,
        [tabType]: {
          ...prev[tabType],
          status: 'error',
          message: error.response?.error || error.message || 'Erro ao processar arquivo',
          result: error.response,
        }
      }));
    } finally {
      setLoading(false);
    }
  };

  const resetUpload = (tabType) => {
    setUploadStatus(prev => ({
      ...prev,
      [tabType]: {
        file: null,
        progress: 0,
        status: null,
        message: '',
        result: null,
      }
    }));
    if (fileInputRefs[tabType]?.current) {
      fileInputRefs[tabType].current.value = '';
    }
  };

  // ========================================
  // Render: Upload zone for ALUNOS
  // ========================================
  const renderAlunosUpload = () => {
    const status = uploadStatus.alunos;
    const config = uploadConfigs.alunos;

    return (
      <div className={`rounded-2xl p-6 backdrop-blur-xl border ${darkMode
        ? 'bg-slate-800/70 border-slate-700/10'
        : 'bg-white/90 border-slate-200 shadow-sm'
        }`}>
        <h3 className={`text-lg font-semibold mb-5 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
          {config.title}
        </h3>

        {/* Class Selector */}
        <div className={`p-4 rounded-xl mb-5 ${darkMode
          ? 'bg-[#2E4974]/10 border border-[#2E4974]/20'
          : 'bg-[#2E4974]/10 border border-[#2E4974]/30'
          }`}>
          <h5 className={`text-sm font-semibold mb-3 text-[#2E4974]`}>Selecione a Turma *</h5>
          <SearchableSelect
            value={selectedClassId}
            onChange={setSelectedClassId}
            options={availableClasses.map(cls => ({
              value: cls.id,
              label: `${cls.class_name}${cls.school_name ? ` - ${cls.school_name}` : ''}${cls.grade ? ` (${cls.grade})` : ''}`,
            }))}
            placeholder="-- Selecione uma turma --"
            darkMode={darkMode}
            className="w-full"
          />
          <p className={`text-xs mt-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Todos os alunos importados serão associados a esta turma
          </p>
        </div>

        {/* Download Template */}
        <div className={`flex items-center justify-between p-4 rounded-xl mb-5 ${darkMode
          ? 'bg-[#13983D]/10 border border-[#13983D]/20'
          : 'bg-[#13983D]/10 border border-[#13983D]/30'
          }`}>
          <div>
            <h5 className="text-sm font-semibold text-[#13983D]">Modelo de Planilha</h5>
            <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Baixe o modelo Excel compativel com planilhas do governo
            </p>
          </div>
          <button
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#13983D]/20 text-[#13983D] hover:bg-[#13983D]/30 transition-all"
            onClick={async () => {
              try {
                const response = await fetch(`${API_BASE_URL}/students/download_template/`, {
                  credentials: 'include',
                });
                if (response.ok) {
                  const blob = await response.blob();
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'template_importacao_alunos.xlsx';
                  link.click();
                  URL.revokeObjectURL(url);
                }
              } catch (error) {
                console.error('Erro ao baixar template:', error);
              }
            }}
          >
            Baixar Template Excel
          </button>
        </div>

        {/* Upload Zone */}
        {renderDropZone('alunos', status, config)}

        {/* Progress + Status */}
        {renderProgressAndStatus('alunos', status)}

        {/* Instructions */}
        <div className={`mt-5 p-4 rounded-xl ${darkMode
          ? 'bg-[#0974BA]/10 border border-[#0974BA]/20'
          : 'bg-[#0974BA]/10 border border-[#0974BA]/30'
          }`}>
          <h5 className="text-sm font-semibold mb-3 text-[#0974BA]">Colunas obrigatorias:</h5>
          <ul className={`text-xs space-y-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {config.columns.map(col => (
              <li key={col.name} className="flex items-start gap-2">
                <span className={`font-mono px-1.5 py-0.5 rounded ${col.required
                  ? 'bg-[#F9B047]/20 text-[#F9B047]'
                  : darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                  }`}>
                  {col.name}
                </span>
                <span>- {col.desc}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        {renderActions('alunos', status)}
      </div>
    );
  };

  // ========================================
  // Render: Upload zone for GABARITOS OMR
  // ========================================
  const renderGabaritosUpload = () => {
    const status = uploadStatus.gabaritos;
    const config = uploadConfigs.gabaritos;

    return (
      <div className={`rounded-2xl p-6 backdrop-blur-xl border ${darkMode
        ? 'bg-slate-800/70 border-slate-700/10'
        : 'bg-white/90 border-slate-200 shadow-sm'
        }`}>
        <h3 className={`text-lg font-semibold mb-5 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
          {config.title}
        </h3>

        {/* Info Banner */}
        <div className={`p-4 rounded-xl mb-5 ${darkMode
          ? 'bg-[#8B5CF6]/10 border border-[#8B5CF6]/20'
          : 'bg-[#8B5CF6]/10 border border-[#8B5CF6]/30'
          }`}>
          <h5 className="text-sm font-semibold text-[#8B5CF6] mb-2">Como funciona?</h5>
          <ol className={`text-xs space-y-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <li>1. Imprima os cartões de Gabarito na página de <strong>Provas</strong></li>
            <li>2. Os alunos preenchem os cartões com caneta azul ou preta</li>
            <li>3. Digitalize os cartões preenchidos em <strong>PDF</strong> (150 DPI+)</li>
            <li>4. Selecione a <strong>prova</strong> e a <strong>turma</strong> abaixo</li>
            <li>5. Faça upload do PDF e o sistema lê automaticamente:</li>
            <li className="ml-4">- CPF do aluno (11 dígitos)</li>
            <li className="ml-4">- Respostas selecionadas (A-E)</li>
          </ol>
        </div>

        {/* Exam Selector */}
        <div className={`p-4 rounded-xl mb-4 ${darkMode
          ? 'bg-[#0974BA]/10 border border-[#0974BA]/20'
          : 'bg-[#0974BA]/10 border border-[#0974BA]/30'
          }`}>
          <h5 className="text-sm font-semibold mb-3 text-[#0974BA]">Selecione a Prova *</h5>
          <SearchableSelect
            value={selectedExamId}
            onChange={setSelectedExamId}
            options={availableExams.map(exam => ({
              value: exam.id,
              label: `${exam.exam_code || exam.exam_name} - ${exam.exam_name} (${exam.total_questions || '?'} questões)`,
            }))}
            placeholder="-- Selecione uma prova --"
            darkMode={darkMode}
            className="w-full"
          />
        </div>

        {/* Class Selector */}
        <div className={`p-4 rounded-xl mb-5 ${darkMode
          ? 'bg-[#2E4974]/10 border border-[#2E4974]/20'
          : 'bg-[#2E4974]/10 border border-[#2E4974]/30'
          }`}>
          <h5 className="text-sm font-semibold mb-3 text-[#2E4974]">Selecione a Turma *</h5>
          <SearchableSelect
            value={selectedClassId}
            onChange={setSelectedClassId}
            options={availableClasses.map(cls => ({
              value: cls.id,
              label: `${cls.class_name}${cls.school_name ? ` - ${cls.school_name}` : ''}${cls.grade ? ` (${cls.grade})` : ''}`,
            }))}
            placeholder="-- Selecione uma turma --"
            darkMode={darkMode}
            className="w-full"
          />
        </div>

        {/* Upload Zone */}
        {renderDropZone('gabaritos', status, config)}

        {/* Progress + Status */}
        {renderProgressAndStatus('gabaritos', status)}

        {/* OMR Results Detail */}
        {status.result && status.result.details && status.result.details.length > 0 && (
          <div className={`mt-5 rounded-xl overflow-hidden border ${darkMode
            ? 'border-slate-700/30'
            : 'border-slate-200'
            }`}>
            <div className={`px-4 py-3 text-sm font-semibold ${darkMode
              ? 'bg-slate-700/50 text-slate-200'
              : 'bg-slate-100 text-slate-700'
              }`}>
              Resultado por Cartão ({status.result.processed || 0} processados)
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}>
                    <th className={`px-4 py-2 text-left text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Pág.</th>
                    <th className={`px-4 py-2 text-left text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>CPF</th>
                    <th className={`px-4 py-2 text-left text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Status</th>
                    <th className={`px-4 py-2 text-left text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Detalhes</th>
                  </tr>
                </thead>
                <tbody>
                  {status.result.details.map((detail, i) => (
                    <tr key={i} className={`border-t ${darkMode ? 'border-slate-700/30' : 'border-slate-100'}`}>
                      <td className={`px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {detail.page}
                      </td>
                      <td className={`px-4 py-2 font-mono text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {detail.student_id || '-'}
                      </td>
                      <td className="px-4 py-2">
                        {detail.status === 'success' ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#13983D]/20 text-[#13983D]">Sucesso</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#E83A3C]/20 text-[#E83A3C]">Erro</span>
                        )}
                      </td>
                      <td className={`px-4 py-2 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {detail.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Summary footer */}
            <div className={`px-4 py-3 flex gap-6 text-xs border-t ${darkMode
              ? 'bg-slate-800/30 border-slate-700/30 text-slate-400'
              : 'bg-slate-50 border-slate-200 text-slate-500'
              }`}>
              <span>Total: <strong>{status.result.processed || 0}</strong></span>
              <span className="text-[#13983D]">Salvos: <strong>{status.result.saved || 0}</strong></span>
              <span className="text-[#E83A3C]">Erros: <strong>{status.result.errors?.length || 0}</strong></span>
            </div>
          </div>
        )}

        {/* Actions */}
        {renderActions('gabaritos', status)}
      </div>
    );
  };

  // ========================================
  // Shared components
  // ========================================
  const renderDropZone = (tabType, status, config) => (
    <div
      className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer ${darkMode
        ? 'border-slate-600/50 bg-slate-900/30 hover:border-[#0974BA] hover:bg-[#0974BA]/5'
        : 'border-slate-300 bg-slate-50 hover:border-[#0974BA] hover:bg-[#0974BA]/5'
        } ${status.status === 'uploading' ? 'pointer-events-none opacity-50' : ''}`}
      onClick={() => fileInputRefs[tabType]?.current?.click()}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, tabType)}
    >
      <input
        ref={fileInputRefs[tabType]}
        type="file"
        className="hidden"
        accept={config.acceptedFormats.join(',')}
        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0], tabType)}
      />

      {status.file ? (
        <div className="space-y-3">
          <div className="text-4xl">{tabType === 'gabaritos' ? '📋' : '📄'}</div>
          <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            {status.file.name}
          </p>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {(status.file.size / 1024).toFixed(2)} KB
          </p>
        </div>
      ) : (
        <>
          <div className="text-5xl mb-4">{tabType === 'gabaritos' ? '📋' : '📁'}</div>
          <h4 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            {tabType === 'gabaritos'
              ? 'Arraste o PDF dos cartões de Gabarito aqui'
              : 'Arraste sua planilha aqui'}
          </h4>
          <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            ou clique para selecionar um arquivo
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            {config.acceptedFormats.map(format => (
              <span
                key={format}
                className="px-3 py-1 rounded-full text-xs font-medium bg-[#0974BA]/20 text-[#0974BA]"
              >
                {format.toUpperCase()}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderProgressAndStatus = (tabType, status) => (
    <>
      {/* Progress bar */}
      {status.status === 'uploading' && (
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
              {tabType === 'gabaritos' ? 'Processando cartões de Gabarito...' : 'Processando...'}
            </span>
            <span className={darkMode ? 'text-slate-300' : 'text-slate-600'}>{status.progress}%</span>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
            <div
              className="h-full bg-gradient-to-r from-[#0974BA] to-[#2E4974] transition-all duration-300"
              style={{ width: `${status.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Status message */}
      {status.message && (
        <div className={`mt-4 p-4 rounded-xl ${status.status === 'success'
          ? darkMode ? 'bg-[#13983D]/10 border border-[#13983D]/20' : 'bg-[#13983D]/10 border border-[#13983D]/30'
          : status.status === 'error'
            ? darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/10 border border-[#E83A3C]/30'
            : darkMode ? 'bg-[#0974BA]/10 border border-[#0974BA]/20' : 'bg-[#0974BA]/10 border border-[#0974BA]/30'
          }`}>
          <p className={`text-sm font-medium ${status.status === 'success'
            ? 'text-[#13983D]'
            : status.status === 'error'
              ? 'text-[#E83A3C]'
              : 'text-[#0974BA]'
            }`}>
            {status.status === 'success' ? '✓' : status.status === 'error' ? '✕' : '•'} {status.message}
          </p>

          {/* Generic result details (for alunos) */}
          {tabType === 'alunos' && status.result && (
            <div className={`mt-3 text-xs space-y-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {(status.result.details?.created ?? status.result.created) !== undefined && (
                <p>Criados: {status.result.details?.created ?? status.result.created}</p>
              )}
              {(status.result.details?.updated ?? status.result.updated) !== undefined && (
                <p>Atualizados: {status.result.details?.updated ?? status.result.updated}</p>
              )}
              {(status.result.details?.errors?.length > 0 || status.result.errors?.length > 0) && (
                <div className="mt-2">
                  <p className="text-[#E83A3C]">
                    Erros ({status.result.details?.errors?.length || status.result.errors?.length}):
                  </p>
                  <ul className="list-disc list-inside max-h-32 overflow-y-auto mt-1">
                    {(status.result.details?.errors || status.result.errors || []).slice(0, 10).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {(status.result.details?.errors?.length || status.result.errors?.length || 0) > 10 && (
                      <li>... e mais {(status.result.details?.errors?.length || status.result.errors?.length) - 10} erros</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  const renderActions = (tabType, status) => (
    <div className="mt-6 flex gap-3 justify-end border-t border-slate-700/10 pt-5">
      <button
        onClick={() => resetUpload(tabType)}
        className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
          ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
          : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
          }`}
        disabled={loading}
      >
        Limpar
      </button>
      <button
        onClick={() => handleUpload(tabType)}
        disabled={!status.file || loading || status.status === 'uploading'}
        className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${!status.file || loading || status.status === 'uploading'
          ? 'bg-slate-500 cursor-not-allowed'
          : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25 hover:-translate-y-0.5'
          }`}
      >
        {status.status === 'uploading' ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Processando...
          </span>
        ) : tabType === 'gabaritos' ? (
          'Processar Gabaritos'
        ) : (
          'Processar Upload'
        )}
      </button>
    </div>
  );

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <>
      {/* Header */}
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
            Importações
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Importe alunos e processe gabaritos
            {effectiveCity?.name && <span className="text-sideb-blue"> - {effectiveCity.name}</span>}
          </p>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Escolas', value: stats.schools, icon: '🏫' },
          { label: 'Turmas', value: stats.classes, icon: '👥' },
          { label: 'Alunos', value: stats.students, icon: '🎓' },
          { label: 'Provas', value: stats.exams, icon: '📝' },
        ].map(stat => (
          <div
            key={stat.label}
            className={`rounded-xl p-4 text-center ${darkMode
              ? 'bg-slate-800/50 border border-slate-700/10'
              : 'bg-white/80 border border-slate-200 shadow-sm'
              }`}
          >
            <div className="text-2xl mb-1">{stat.icon}</div>
            <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
              {stat.value.toLocaleString('pt-BR')}
            </div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('alunos')}
          className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${activeTab === 'alunos'
            ? 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] text-white shadow-lg shadow-[#0974BA]/20'
            : darkMode
              ? 'bg-slate-800/50 text-slate-400 hover:text-slate-200 border border-slate-700/10'
              : 'bg-white/80 text-slate-500 hover:text-slate-700 border border-slate-200'
            }`}
        >
          Importar Alunos
        </button>
        {!isReferenciaTecnica && (
        <button
          onClick={() => setActiveTab('gabaritos')}
          className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${activeTab === 'gabaritos'
            ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-lg shadow-[#8B5CF6]/20'
            : darkMode
              ? 'bg-slate-800/50 text-slate-400 hover:text-slate-200 border border-slate-700/10'
              : 'bg-white/80 text-slate-500 hover:text-slate-700 border border-slate-200'
            }`}
        >
          Upload Gabaritos
        </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'alunos' && renderAlunosUpload()}
      {activeTab === 'gabaritos' && renderGabaritosUpload()}
    </>
  );
}

export default Inputs;
